import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import { getGuideAccent } from "../../utils/guideAccent";

const Template311 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const portadaImage = getImageSrc("portada");
	const torreSticker = getCanvasImageUrl("torre-sticker");
	const torreSticker2Entry = page.canvasImages?.find(
		(img) => img.id === "torre-sticker-2",
	);
	const torreSticker2 = torreSticker2Entry?.notShow
		? undefined
		: torreSticker2Entry?.imageUrl;

	const introText = page.texts?.[1]?.content;
	const subtitle = page.texts?.[2]?.content;
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const reserva =
		page?.reserva ?? page?.data?.reserva ?? page?.data?.data?.reserva;
	const llegar = page?.llegar ?? page?.data?.llegar ?? page?.data?.data?.llegar;
	const reservaOnSticker = sections.some((section) => section?.stickerCanvasId);

	// Lógica de color del header
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	let actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);
	const contentColor = textColor || "#444";

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* IMAGEN PORTADA */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				<img
					src={portadaImage}
					alt="Londres"
					style={{ width: "100%", height: 200, objectFit: "cover" }}
				/>
				{subtitle && (
					<Box
						component="h1"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: isSmallScreen ? "24px" : "30px",
							textAlign: isSmallScreen ? "left" : "center",
							position: "absolute",
							bottom: "0",
							margin: "10px",
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
						}}
					>
						{subtitle}
					</Box>
				)}
				<img
					src={torreSticker}
					alt="Tower Bridge"
					style={{
						width: isSmallScreen ? 90 : 100,
						position: "absolute",
						bottom: isSmallScreen ? 50 : 60,
						left: 20,
					}}
				/>
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					padding: "10px 12px",
					paddingBottom: "5px",
					position: "relative",
					marginTop: "0px !important",
					paddingTop: "4px !important",
					flex: 1,
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
				}}
			>
				{introText && (
					<Box
						component="div"
						dangerouslySetInnerHTML={{ __html: introText }}
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							marginTop: "-10px",
							paddingTop: "0px",
							padding: 0,
						}}
					/>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						marginTop: "0px",
						paddingTop: "0px",
						flex: 1,
						minHeight: 0,
						overflow: "visible",
					}}
				>
					{sections.map((section, idx) => {
						const imageSrc =
							section.layout === "box"
								? getCanvasImageUrl(section.imageId)
								: getImageSrc(section.imageId);
						if (section.layout === "box") {
							return (
								<Box
									key={idx}
									data-block-id={`text-${idx + 2}`}
									sx={{
										margin: 0,
										marginTop: isSmallScreen ? "50px" : 0,
										padding: 0,
									}}
								>
									<Box
										sx={{ display: "flex", gap: "16px", position: "relative" }}
									>
										<Box sx={{ width: "100%" }}>
											<Box
												component="p"
												sx={{
													...estiloP,
													padding: "12px",
													backgroundColor: accent.boxBg,
													borderRadius: "12px",
												}}
											>
												{imageSrc && (
													<img
														src={imageSrc}
														alt={section.title}
														style={{
															width: isSmallScreen ? 105 : 115,
															float: "right",
															marginRight: -34,
															marginTop: -25,
														}}
													/>
												)}
												{section.title && (
													<Box component="h3" sx={estiloH3}>
														{section.title}
													</Box>
												)}
												{section.body}
											</Box>
										</Box>
									</Box>
								</Box>
							);
						}
						return (
							<Box
								key={idx}
								data-block-id={`text-${idx + 2}`}
								sx={{
									display: "flex",
									gap: "16px",
									alignItems: "flex-start",
									position: "relative",
									overflow: "visible",
									margin: 0,
									padding: 0,
								}}
							>
								{imageSrc && (
									<Box
										sx={{
											width: "40%",
											position: "relative",
											flexShrink: 0,
											overflow: "visible",
											lineHeight: 0,
											fontSize: 0,
										}}
									>
										<img
											src={imageSrc}
											alt={section.title}
											style={{
												width: "100%",
												height: 110,
												objectFit: "cover",
												borderRadius: "12px",
												display: "block",
											}}
										/>
										{section.stickerCanvasId &&
											!section.showReserva &&
											getCanvasImageUrl(section.stickerCanvasId) && (
												<img
													src={getCanvasImageUrl(section.stickerCanvasId)}
													alt=""
													style={{
														width: 86,
														position: "absolute",
														bottom: isSmallScreen ? -20 : 9,
														left: isSmallScreen ? -30 : -10,
														zIndex: 5,
													}}
												/>
											)}
									</Box>
								)}
								{reserva &&
									section.showReserva &&
									getCanvasImageUrl(section.stickerCanvasId) && (
										<>
											<img
												src={getCanvasImageUrl(section.stickerCanvasId)}
												alt=""
												style={{
													width: 88,
													position: "absolute",
													right: -30,
													bottom: isSmallScreen ? -73 : -53,
													zIndex: 5,
												}}
											/>
											<Box
												sx={{
													position: "absolute",
													right: 50,
													bottom: isSmallScreen ? -60 : -30,
													backgroundColor: "#fff8db",
													border: "2px solid #f5c400",
													borderRadius: "16px 16px 0px 16px",
													padding: "10px 12px",
													fontSize: 11,
													fontFamily: `${
														fontFamily || "'Poppins', sans-serif"
													}`,
													color: contentColor,
													boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
													width: isSmallScreen ? 170 : 200,
													zIndex: 6,
												}}
											>
												<a
													href={reserva.url}
													target="_blank"
													rel="noopener noreferrer"
													style={{
														color: "#000000",
														fontWeight: 700,
														textDecoration: "underline",
													}}
												>
													{reserva.text}
												</a>{" "}
												{reserva.suffix}
											</Box>
										</>
									)}
								{section.sabiasQue &&
									section.sabiasQue.map((sq, i) => (
										<Box
											key={i}
											sx={{
												position: "absolute",
												top: i === 0 ? -10 : 30 + (i - 1) * 2,
												left: "calc(40% - 22px)",
											}}
										>
											<SabiasQue icono={sq.icono} texto={sq.texto} />
										</Box>
									))}
								<Box
									sx={{
										width: "60%",
										paddingRight: section.showReserva ? "120" : 0,
										boxSizing: "border-box",
									}}
								>
									{section.title && (
										<Box component="h3" sx={estiloH3}>
											{section.title}
										</Box>
									)}
									{section.body && (
										<Box
											component="p"
											sx={{ ...estiloP, fontSize: 13, color: contentColor }}
										>
											{section.body}
										</Box>
									)}
								</Box>
							</Box>
						);
					})}

					{llegar && llegar.title && (
						<Box
							data-block-id="text-6"
							sx={{
								display: "flex",
								flex: isSmallScreen ? "none" : 1,
								minHeight: isSmallScreen ? "auto" : 0,
								marginTop: 0,
							}}
						>
							<Box
								sx={{
									backgroundColor: accent.boxBg,
									border: `3px solid ${accent.boxBorder}`,
									borderRadius: "12px",
									padding: "8px 20px",
									fontSize: 12,
									fontFamily: `${
										fontFamily || "'Poppins', sans-serif"
									} !important`,
									color: "#333",
									marginBottom: isSmallScreen ? "12px" : 0,
									width: "100%",
									height: isSmallScreen ? "auto" : "100%",
									boxSizing: "border-box",
								}}
							>
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: "8px",
										fontSize: 13,
										fontWeight: 700,
										display: "flex",
										alignItems: "center",
										gap: 6,
										color: accent.accent,
										textTransform: "uppercase",
									}}
								>
									{llegar.title}
								</Box>
								{llegar.content && (
									<Box
										component="div"
										dangerouslySetInnerHTML={{ __html: llegar.content }}
										sx={{
											paddingLeft: "0px",
											margin: 0,
											lineHeight: 1.5,
											"& ul": { paddingLeft: 4, margin: 0 },
										}}
									/>
								)}
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template311.thumbnail = "/images/template-1.png";

export default Template311;
